import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsRenderer,
  InfoWindow,
  OverlayView,
} from "@react-google-maps/api";
import Layouts from "../../layouts/layouts";

const containerStyle = {
  width: "100vw",
  height: "100vh",
  position: "absolute",
  top: 0,
  left: 0,
};
const rippleStyle = {
  position: "absolute",
  width: "200px",
  height: "200px",
  background: "#4285F4",
  borderRadius: "50%",
  animation: "ripple 1.5s infinite",
  transform: "translate(-50%, -50%)",
  opacity: 0.4,
};

const keyframes = `
  @keyframes ripple {
    0% {
      transform: translate(-50%, -50%) scale(0.3);
      opacity: 0.6;
    }

    100% {
      transform: translate(-50%, -50%) scale(1.4);
      opacity: 0;
    }
  }
`;

export default function UserTracking() {
  const { userId } = useParams();
  const mapRef = useRef(null);

  const [pathCoordinates, setPathCoordinates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [directions, setDirections] = useState(null);
  const [noTrackingMessage, setNoTrackingMessage] = useState("");
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);
  const chunkCoordinates = (coords, chunkSize = 25) => {
    const chunks = [];
    for (let i = 0; i < coords.length; i += chunkSize) {
      chunks.push(coords.slice(i, i + chunkSize));
    }
    return chunks;
  };

  useEffect(() => {
    fetchUserTrackingData();
  }, []);
  const fetchUserTrackingData = async () => {
    try {
      setLoading(true);
      setNoTrackingMessage("");
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-tracking/${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const trackingData = response.data.data || [];

      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split("T")[0];

      // Filter coordinates from the current date only
      const filteredCoordinates = trackingData
        .filter((entry) => entry.createdAt.split("T")[0] === today)
        .map((entry) => ({
          lat: parseFloat(entry.tracking_coordinates.latitude),
          lng: parseFloat(entry.tracking_coordinates.longitude),
          locationName: entry.id_user.name || "Unknown User",
          timestamp: new Date(entry.createdAt).toLocaleString(),
        }));

      if (filteredCoordinates.length === 0) {
        setPathCoordinates([]);
        setNoTrackingMessage("NO TRACKING AVAILABLE FOR TODAY");
        return;
      }

      setPathCoordinates(filteredCoordinates);
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pathCoordinates.length > 1) {
      fetchDirections(pathCoordinates);
    }
  }, [pathCoordinates]);

  const fetchDirections = async (coordinates) => {
    if (coordinates.length < 2) return;

    const directionPromises = [];

    const chunks = chunkCoordinates(coordinates, 25);

    chunks.forEach((chunk) => {
      if (chunk.length >= 2) {
        const origin = chunk[0];
        const destination = chunk[chunk.length - 1];
        const waypoints = chunk.slice(1, -1).map((coord) => ({
          location: { lat: coord.lat, lng: coord.lng },
          stopover: false,
        }));

        const directionsService = new window.google.maps.DirectionsService();
        const request = new Promise((resolve, reject) => {
          directionsService.route(
            {
              origin,
              destination,
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                resolve(result);
              } else {
                reject(`Failed with status: ${status}`);
              }
            },
          );
        });

        directionPromises.push(request);
      }
    });

    try {
      const results = await Promise.all(directionPromises);
      setDirections(results);
    } catch (error) {
      console.error("Error fetching multiple directions:", error);
    }
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleInfoWindowCloseClick = () => {
    setSelectedMarker(null);
  };

  return (
    <>
      <Layouts />
      <LoadScript googleMapsApiKey={process.env.REACT_APP_BASE_API}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={pathCoordinates[0] || { lat: 30.7067034, lng: 76.6877915 }}
          zoom={12}
          onLoad={(map) => (mapRef.current = map)}
        >
          {loading ? (
            <div>Loading...</div>
          ) : noTrackingMessage ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "#ffdddd",
                padding: "10px 20px",
                borderRadius: "10px",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#d9534f",
              }}
            >
              {noTrackingMessage}
            </div>
          ) : (
            <>
              {pathCoordinates.length > 0 && (
                <OverlayView
                  position={pathCoordinates[pathCoordinates.length - 1]}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={rippleStyle}></div>
                </OverlayView>
              )}

              {pathCoordinates.map((coord, index) => (
                <Marker
                  key={index}
                  position={coord}
                  label={(index + 1).toString()}
                  onClick={() => handleMarkerClick(coord)}
                />
              ))}

              {Array.isArray(directions) &&
                directions.map((dir, index) => (
                  <DirectionsRenderer key={index} directions={dir} />
                ))}

              {selectedMarker && (
                <InfoWindow
                  position={selectedMarker}
                  onCloseClick={handleInfoWindowCloseClick}
                >
                  <div>
                    <h4>{selectedMarker.locationName}</h4>
                    <p>
                      Coordinates: {selectedMarker.lat}, {selectedMarker.lng}
                    </p>
                    <p>Time: {selectedMarker.timestamp}</p>
                  </div>
                </InfoWindow>
              )}
            </>
          )}
        </GoogleMap>
      </LoadScript>
    </>
  );
}
